interface PasswordStrengthProps {
  password: string;
}

interface StrengthResult {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  color: string;
  checks: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
  };
}

const LABELS: Record<number, string> = {
  0: 'Muito fraca',
  1: 'Fraca',
  2: 'Razoável',
  3: 'Boa',
  4: 'Forte',
};

const COLORS: Record<number, string> = {
  0: '#ef4444',
  1: '#f97316',
  2: '#eab308',
  3: '#22c55e',
  4: '#16a34a',
};

export function analyzePassword(password: string): StrengthResult {
  const checks = {
    minLength:    password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber:    /[0-9]/.test(password),
    hasSpecial:   /[!@#$%^&*()_+\-=\[\]{}|;':,./<>?]/.test(password),
  };

  const passing = Object.values(checks).filter(Boolean).length;
  const rawScore = Math.min(passing, 4) as 0 | 1 | 2 | 3 | 4;

  return {
    score: rawScore,
    label: LABELS[rawScore],
    color: COLORS[rawScore],
    checks,
  };
}

const CRITERIA: { key: keyof StrengthResult['checks']; label: string }[] = [
  { key: 'minLength',    label: 'Mínimo 8 caracteres' },
  { key: 'hasUppercase', label: 'Letra maiúscula' },
  { key: 'hasLowercase', label: 'Letra minúscula' },
  { key: 'hasNumber',    label: 'Número' },
  { key: 'hasSpecial',   label: 'Caractere especial' },
];

export function PasswordStrength({ password }: PasswordStrengthProps) {
  if (password.length === 0) return null;

  const result = analyzePassword(password);

  return (
    <div style={{ marginTop: '0.5rem' }}>
      {/* Segment bars */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '0.5rem' }}>
        {[0, 1, 2, 3].map(idx => (
          <div
            key={idx}
            style={{
              flex: '1 1 0',
              height: '4px',
              borderRadius: '2px',
              backgroundColor: idx < result.score ? result.color : '#e5e7eb',
              transition: 'background-color 200ms ease',
            }}
          />
        ))}
      </div>

      {/* Label + criteria */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem 1rem' }}>
        <span
          style={{
            fontSize: 'var(--text-caption)',
            fontWeight: 600,
            color: result.color,
          }}
        >
          {result.label}
        </span>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem 0.75rem' }}>
          {CRITERIA.map(({ key, label }) => {
            const passes = result.checks[key];
            return (
              <span
                key={key}
                style={{
                  fontSize: 'var(--text-caption)',
                  color: passes ? '#22c55e' : '#9ca3af',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.2rem',
                  transition: 'color 200ms ease',
                }}
              >
                <span style={{ fontWeight: 700 }}>{passes ? '✓' : '✗'}</span>
                {label}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
